
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { buttonVariants } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { 
  Users, 
  LayoutDashboard, 
  Building, 
  FileText, 
  ShieldCheck,
  ListTodo
} from "lucide-react";
import { UserRole } from "@/lib/types";
import { cn } from "@/lib/utils";

interface NavLinksProps {
  onClick?: () => void;
  isMobile?: boolean;
}

const NavLinks = ({ onClick, isMobile = false }: NavLinksProps) => {
  const { profile } = useAuth();
  const role = profile?.role as UserRole || 'member';
  const location = useLocation();
  
  const navItems = [
    { 
      icon: Users, 
      label: 'Leads', 
      to: '/app', 
      adminOnly: false,
      iconColor: 'text-blue-500',
      activeGradient: 'from-blue-500/10 to-blue-500/5'
    },
    { 
      icon: LayoutDashboard, 
      label: 'Dashboard', 
      to: '/dashboard', 
      adminOnly: false,
      iconColor: 'text-purple-500',
      activeGradient: 'from-purple-500/10 to-purple-500/5'
    },
    { 
      icon: ListTodo, 
      label: 'Tasks', 
      to: '/tasks', 
      adminOnly: false,
      iconColor: 'text-green-500',
      activeGradient: 'from-green-500/10 to-green-500/5'
    },
    { 
      icon: Building, 
      label: 'Companies', 
      to: '/companies', 
      adminOnly: false,
      iconColor: 'text-orange-500',
      activeGradient: 'from-orange-500/10 to-orange-500/5'
    },
    { 
      icon: FileText, 
      label: 'Documentation', 
      to: '/documentation', 
      adminOnly: false,
      iconColor: 'text-cyan-500',
      activeGradient: 'from-cyan-500/10 to-cyan-500/5'
    },
    { 
      icon: ShieldCheck, 
      label: 'Admin', 
      to: '/admin', 
      adminOnly: true,
      iconColor: 'text-red-500',
      activeGradient: 'from-red-500/10 to-red-500/5'
    }
  ];

  const renderNavLinks = (navLinks: typeof navItems) => {
    return navLinks
      .filter(item => !item.adminOnly || role === 'admin')
      .map((item) => {
        const IconComponent = item.icon;
        
        if (isMobile) {
          return (
            <Link
              key={item.label}
              to={item.to}
              className="flex items-center gap-3 w-full p-3 rounded-md hover:bg-accent transition-colors"
              onClick={onClick}
            >
              <IconComponent className="h-5 w-5" />
              <span className="text-base text-left w-full">{item.label}</span>
            </Link>
          );
        }
        
        const isActive = location.pathname === item.to;
        
        return (
          <Link
            key={item.label}
            to={item.to}
            className={cn(
              buttonVariants({ variant: "ghost" }),
              "w-full justify-start h-11 px-3 group relative overflow-hidden transition-all duration-200",
              "hover:bg-gradient-to-r hover:from-primary/10 hover:to-primary/5",
              "hover:shadow-sm hover:border-l-2 hover:border-primary",
              isActive && [
                "bg-gradient-to-r",
                item.activeGradient,
                "border-l-2 border-primary",
                "shadow-sm"
              ]
            )}
            onClick={onClick}
          >
            <IconComponent 
              className={cn(
                "h-5 w-5 mr-3 transition-all duration-200",
                item.iconColor,
                "group-hover:scale-110"
              )} 
            />
            <span className="text-sm font-medium">{item.label}</span>
            
            {/* Active indicator dot */}
            {isActive && (
              <span className="absolute right-3 h-2 w-2 rounded-full bg-primary animate-pulse" />
            )}
          </Link>
        );
      });
  };
  
  return (
    <div className="flex flex-col gap-2 w-full">
      {renderNavLinks(navItems)}
    </div>
  );
};

export default NavLinks;
